import { AddIcon } from '@chakra-ui/icons'
import {
  Checkbox,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { toPlural } from '@chem-po/core'
import {
  colors,
  EncounterTypeConfig,
  encounterTypeName,
  examTypeConfigurationFields,
  FieldMap,
  FieldTypes,
  isField,
} from '@hb/shared'
import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { requestExamPdf } from '../../../../backend/functions/other'
import { downloadFromStorage } from '../../../../backend/storage/utils'
import { ActionButton, SolidActionButton } from '../../../../components/Buttons/ActionButton'
import { SimpleForm, StandaloneInput } from '../../../../components/forms'
import { useProfilePopoverMaxHeight } from '../../../../components/Users/Profile/hooks/useProfilePopoverMaxHeight'
import { useExamListGroup } from '../../context'
import { EncounterSelection } from '../../types'
import { useExamList } from '../context'
import { encounterHeaderAddonRecord } from './EncounterHeaderAddon'

const AddEncounterButton = ({
  type,
  onOpenExam,
}: {
  type: EncounterSelection['type']
  onOpenExam: (exam: EncounterSelection) => void
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { maxHeight, containerRef } = useProfilePopoverMaxHeight(isOpen)

  const configFieldMap = useMemo<FieldMap | null>(() => {
    const configFields = examTypeConfigurationFields[type]
    if (!configFields) return null
    return { name: '', children: configFields }
  }, [type])

  const singleDropdownField = useMemo(() => {
    if (!configFieldMap) return null
    const entries = Object.entries(configFieldMap.children)
    if (entries.length !== 1) return null
    const [fieldId, field] = entries[0]
    if (!isField(field) || field.type !== FieldTypes.DROPDOWN) return null
    return { fieldId, field }
  }, [configFieldMap])

  const handleClick = useCallback(() => {
    if (configFieldMap) {
      onOpen()
    } else {
      onOpenExam({ type, examId: null, config: null })
    }
  }, [configFieldMap, onOpen, onOpenExam, type])

  const handleConfigSubmit = useCallback(
    async (values: EncounterTypeConfig) => {
      onOpenExam({ type, examId: null, config: values })
      onClose()
      return undefined
    },
    [onClose, onOpenExam, type],
  )

  return (
    <Popover
      closeOnBlur={false}
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      isLazy
      strategy="fixed">
      <PopoverTrigger>
        <SolidActionButton
          size="xs"
          fontSize="sm"
          borderRadius="full"
          pl={3}
          leftIcon={<AddIcon filter="drop-shadow(1px 1px 3px #00000077)" w={3} h={3} />}
          onClick={handleClick}>
          {encounterTypeName[type]}
        </SolidActionButton>
      </PopoverTrigger>
      {configFieldMap ? (
        <Portal>
          <PopoverContent w="auto">
            <PopoverHeader
              bg={colors.green.hex}
              borderTopRadius={6}
              fontFamily="Comfortaa"
              py={1.5}
              fontWeight={600}
              color="white"
              textShadow="1px 1px 3px #00000066">
              New {encounterTypeName[type]}
            </PopoverHeader>
            <PopoverArrow bg={colors.green.hex} />
            <PopoverCloseButton top={1.5} borderRadius="full" bg="blackAlpha.300" color="white" />
            <PopoverBody
              bg="gray.50"
              ref={containerRef}
              display="flex"
              flexFlow="column"
              w="300px"
              maxW="100vw"
              maxH={`${maxHeight}px`}
              h="auto"
              overflowY="auto"
              p={0}>
              {singleDropdownField ? (
                <VStack spacing={3} p={2} align="stretch">
                  <StandaloneInput
                    field={singleDropdownField.field}
                    onChange={value => {
                      if (value) {
                        handleConfigSubmit({
                          [singleDropdownField.fieldId]: value,
                        } as EncounterTypeConfig)
                      }
                    }}
                  />
                </VStack>
              ) : (
                <SimpleForm
                  field={configFieldMap}
                  submitText="Open Note"
                  hasResetButton={false}
                  onCancel={onClose}
                  onSubmit={handleConfigSubmit}
                  boxProps={{ shadow: 'none', bg: 'transparent' }}
                  bodyContainerProps={{ py: 1 }}
                />
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      ) : null}
    </Popover>
  )
}

export const EncounterListHeader = ({ children }: PropsWithChildren) => {
  const {
    type,
    typeName,
    encounterEntries: examEntries,
    practiceId,
    patientId,
    onOpenExam,
  } = useExamList()
  const HeaderAddon = encounterHeaderAddonRecord[type]
  const { isSelecting, selectedIds, onToggleAll } = useExamListGroup()
  const numSelected = selectedIds[type]?.length ?? 0
  const numTotal = examEntries.length
  const allSelected = numTotal > 0 && numSelected === numTotal
  const someSelected = numSelected > 0 && numSelected < numTotal
  const [printingAll, setPrintingAll] = useState(false)

  const handlePrintAll = useCallback(async () => {
    if (!practiceId || examEntries.length === 0 || !patientId) return
    setPrintingAll(true)
    try {
      const { data } = await requestExamPdf({
        practiceId,
        patientId,
        examIds: examEntries.map(e => e.id),
      })
      await downloadFromStorage(data.storagePath)
    } catch (err) {
      console.error('Failed to generate exams PDF', err)
    } finally {
      setPrintingAll(false)
    }
  }, [practiceId, patientId, examEntries])

  return (
    <>
      <Flex
        bg="gray.50"
        borderBottom="1px solid"
        borderColor="gray.200"
        _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
        py={2}
        px={4}
        w="100%"
        align="center"
        // narrow screens: title stays on its own line and the action buttons wrap below
        flexWrap="wrap"
        rowGap={1.5}>
        <Text fontSize="md" fontWeight={500} whiteSpace="nowrap">
          {typeName}
        </Text>
        {children ? (
          <Flex flex={1} minW={0} align="center">
            {children}
          </Flex>
        ) : null}
        {HeaderAddon ? <HeaderAddon encounters={examEntries} /> : null}
        {!isSelecting ? (
          <Flex ml="auto" align="center" gap={2} flexWrap="wrap" rowGap={1.5} justify="flex-end">
            <ActionButton
              size="xs"
              borderRadius="full"
              isLoading={printingAll}
              isDisabled={examEntries.length === 0}
              onClick={handlePrintAll}>
              Print All {toPlural(typeName)}
            </ActionButton>
            <AddEncounterButton type={type} onOpenExam={onOpenExam} />
          </Flex>
        ) : null}
      </Flex>
      {isSelecting ? (
        <Flex bg="#fafafa" borderBottom="1px solid #00000033" w="100%" align="stretch">
          <Text fontSize="sm" color="gray.600" py={2} pl={4} flex={1} alignSelf="center">
            {numSelected}/{numTotal} {toPlural(typeName)} selected
          </Text>
          <Flex
            w="38px"
            justify="center"
            align="center"
            borderLeft="1px solid"
            borderColor="gray.100"
            flexShrink={0}
            cursor={numTotal === 0 ? 'not-allowed' : 'pointer'}
            onClick={() =>
              numTotal > 0 &&
              onToggleAll(
                type,
                examEntries.map(e => e.id),
                allSelected,
              )
            }>
            <Checkbox
              colorScheme="green"
              isChecked={allSelected}
              isIndeterminate={someSelected}
              isDisabled={numTotal === 0}
              pointerEvents="none"
            />
          </Flex>
        </Flex>
      ) : null}
    </>
  )
}
